/*
 * saif-pitchshift.js — reference-clip transposition for the Practice Studio.
 *
 * Public surface (the ENTIRE API):
 *     window.SaifPitchShift.process(url, semitones) -> Promise<string|null>
 *
 * Resolves to an object-URL for a WAV blob that is `semitones` higher/lower
 * than the source clip with its DURATION UNCHANGED (tempo preserved), or null
 * on ANY failure. It never rejects: callers treat null as "keep the original
 * key" so audio and pitch-data never disagree.
 *
 * Algorithm: hand-rolled WSOLA (resample-then-stretch-back), zero dependencies.
 *   1. anti-alias (only when shifting UP)  -> two cascaded RBJ lowpass biquads
 *   2. pitch resample (linear interp, ratio r = 2^(N/12))  -> pitch xR, dur /R
 *   3. WSOLA time-stretch back to EXACTLY the original sample count -> dur restored
 * The heavy loop runs in a Blob-URL Worker; a chunked main-thread fallback
 * covers app-injected CSPs that block blob workers. Blob-URL lifecycle
 * (including revocation) is owned entirely here via a small LRU.
 */
(function () {
  'use strict';

  var TARGET_FS = 22050;   // working sample rate for the OfflineAudioContext mixdown
  var CACHE_CAP = 4;       // retained WAV blobs (~2.65 MB each at 60 s)
  var WATCHDOG_MS = 20000; // terminate a stuck worker after this and fall back to null

  /* ---------------------------------------------------------------- *
   * DSP kernels — pure, self-contained (no outer refs) so they can be
   * serialised into the worker via Function.prototype.toString().
   * ---------------------------------------------------------------- */

  function biquadPass(x, y, b0, b1, b2, a1, a2) {
    var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (var i = 0; i < x.length; i++) {
      var xn = x[i];
      var yn = b0 * xn + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
      x2 = x1; x1 = xn; y2 = y1; y1 = yn;
      y[i] = yn;
    }
  }

  // Two cascaded RBJ lowpass biquads (Q = 1/sqrt(2)) at fc = 0.45*fs/r.
  // Removes content that would alias past Nyquist once resampled up by r.
  function antiAlias(pcm, fs, r) {
    var fc = 0.45 * fs / r;
    if (fc >= fs * 0.5) return pcm;
    var w0 = 2 * Math.PI * fc / fs;
    var cw = Math.cos(w0), sw = Math.sin(w0), K = (1 - cw) / 2;
    // True 4th-order Butterworth = two sections at Q1/Q2 (maximally-flat passband,
    // -3dB at fc) instead of two Q=0.707 sections (-6dB at fc + droop that dulled
    // the presence band once resampled up).
    var qa = sw / (2 * 0.54119610), na = 1 + qa;
    var qb = sw / (2 * 1.30656296), nb = 1 + qb;
    var t = new Float32Array(pcm.length);
    biquadPass(pcm, t, K / na, (1 - cw) / na, K / na, (-2 * cw) / na, (1 - qa) / na);
    var out = new Float32Array(pcm.length);
    biquadPass(t, out, K / nb, (1 - cw) / nb, K / nb, (-2 * cw) / nb, (1 - qb) / nb);
    return out;
  }

  // Windowed-sinc (Lanczos-3, 6-tap) resample reading at step r: pitch xR, length
  // /R. Replaces linear interpolation, whose triangular (sinc^2) kernel drooped
  // the presence band (dull) and folded mid-band spectral images into the audible
  // range (metallic) at large r. Same output length M so WSOLA's target math and
  // the main-thread fallback stay identical; kept the name to avoid re-registering
  // it in WORKER_SRC.
  function resampleLinear(x, r) {
    var L = x.length;
    var M = Math.max(1, Math.floor((L - 1) / r) + 1);
    var y = new Float32Array(M);
    var A = 3, PI = Math.PI;
    for (var i = 0; i < M; i++) {
      var pos = i * r, c = Math.floor(pos), acc = 0, wsum = 0;
      for (var k = -2; k <= 3; k++) {
        var idx = c + k, d = pos - idx, w;
        if (d === 0) w = 1;
        else if (d <= -A || d >= A) w = 0;
        else { var pd = PI * d; w = (A * Math.sin(pd) * Math.sin(pd / A)) / (pd * pd); }
        var s = idx < 0 ? x[0] : (idx >= L ? x[L - 1] : x[idx]);
        acc += w * s; wsum += w;
      }
      y[i] = wsum !== 0 ? acc / wsum : 0;
    }
    return y;
  }

  // WSOLA state for stretching `y` to EXACTLY targetLen samples.
  function wsolaInit(y, targetLen, fs) {
    var scale = fs / 22050;
    var FRAME = Math.max(64, Math.round(1024 * scale));
    // Widen the cross-fade toward ~50% as the stretch grows (large up-shifts), so
    // the frozen verbatim run between seams shrinks and the periodic "robotic"
    // buzz softens. Small / down shifts keep the original 25% (byte-identical).
    var stretch = targetLen / Math.max(1, y.length);
    var ovFrac = stretch > 1 ? Math.min(0.5, 0.25 + 0.25 * (stretch - 1)) : 0.25;
    var OVERLAP = Math.max(16, Math.round(FRAME * ovFrac));
    if (OVERLAP >= FRAME) OVERLAP = FRAME >> 1;
    var SYN_HOP = FRAME - OVERLAP;
    var SEEK = Math.max(0, Math.round(300 * scale));
    var anaHop = Math.max(1, Math.round(SYN_HOP * (y.length / Math.max(1, targetLen))));
    var fadeIn = new Float32Array(OVERLAP);
    for (var i = 0; i < OVERLAP; i++) {
      fadeIn[i] = 0.5 * (1 - Math.cos(Math.PI * (i + 0.5) / OVERLAP));
    }
    return {
      y: y, out: new Float32Array(targetLen), targetLen: targetLen,
      FRAME: FRAME, OVERLAP: OVERLAP, SYN_HOP: SYN_HOP, SEEK: SEEK,
      anaHop: anaHop, fadeIn: fadeIn, k: 0, done: false
    };
  }

  // Advance WSOLA by up to maxFrames overlap-add frames. Returns true when done.
  function wsolaAdvance(st, maxFrames) {
    var y = st.y, out = st.out, yl = y.length, target = st.targetLen;
    var FRAME = st.FRAME, OVERLAP = st.OVERLAP, SYN_HOP = st.SYN_HOP;
    var SEEK = st.SEEK, anaHop = st.anaHop, fadeIn = st.fadeIn;
    var processed = 0, i;
    while (processed < maxFrames) {
      var k = st.k;
      var s = k * SYN_HOP;
      if (s >= target) { st.done = true; break; }
      var a;
      if (k === 0) {
        a = 0; // first frame verbatim, no seek
      } else {
        // Find the analysis offset whose head best matches the output tail
        // we are about to cross-fade into (normalised cross-correlation).
        var aBase = k * anaHop;
        var bestD = 0, bestScore = -Infinity, d;
        for (d = -SEEK; d <= SEEK; d += 4) {
          var as = aBase + d, dot = 0, en = 0;
          for (i = 0; i < OVERLAP; i++) {
            var yv = (as + i >= 0 && as + i < yl) ? y[as + i] : 0;
            var rv = (s + i < target) ? out[s + i] : 0; // last frame: s+i can exceed out length
            dot += yv * rv; en += yv * yv;
          }
          var sc = dot / Math.sqrt(en + 1e-9);
          if (sc > bestScore) { bestScore = sc; bestD = d; }
        }
        var lo = bestD - 3, hi = bestD + 3;
        for (d = lo; d <= hi; d++) {
          if (d < -SEEK || d > SEEK) continue;
          var as2 = aBase + d, dot2 = 0, en2 = 0;
          for (i = 0; i < OVERLAP; i++) {
            var yv2 = (as2 + i >= 0 && as2 + i < yl) ? y[as2 + i] : 0;
            var rv2 = (s + i < target) ? out[s + i] : 0;
            dot2 += yv2 * rv2; en2 += yv2 * yv2;
          }
          var sc2 = dot2 / Math.sqrt(en2 + 1e-9);
          if (sc2 > bestScore) { bestScore = sc2; bestD = d; }
        }
        a = aBase + bestD;
      }
      // Cross-fade the head into the existing tail, copy the remainder verbatim.
      for (i = 0; i < OVERLAP && s + i < target; i++) {
        var hv = (a + i >= 0 && a + i < yl) ? y[a + i] : 0;
        if (k === 0) out[s + i] = hv;
        else out[s + i] = out[s + i] * (1 - fadeIn[i]) + hv * fadeIn[i];
      }
      for (i = OVERLAP; i < FRAME && s + i < target; i++) {
        out[s + i] = (a + i >= 0 && a + i < yl) ? y[a + i] : 0;
      }
      st.k++;
      processed++;
    }
    return st.done;
  }

  // Minimal mono PCM16 WAV (44-byte header).
  function encodeWav(pcm, fs) {
    var n = pcm.length;
    var buf = new ArrayBuffer(44 + n * 2);
    var dv = new DataView(buf);
    function ws(o, s) { for (var i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); }
    ws(0, 'RIFF'); dv.setUint32(4, 36 + n * 2, true); ws(8, 'WAVE');
    ws(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
    dv.setUint32(24, fs, true); dv.setUint32(28, fs * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
    ws(36, 'data'); dv.setUint32(40, n * 2, true);
    var o = 44;
    for (var i = 0; i < n; i++) {
      var v = pcm[i]; v = v < -1 ? -1 : v > 1 ? 1 : v;
      dv.setInt16(o, v < 0 ? v * 0x8000 : v * 0x7FFF, true); o += 2;
    }
    return buf;
  }

  // Radix-2 iterative in-place FFT. Twiddle tables (cosT/sinT, length n) are
  // precomputed by the caller so there's no per-call cos/sin and no accumulated
  // recurrence error. inverse=true divides by n.
  function fft(re, im, inverse, cosT, sinT, n) {
    for (var i = 1, j = 0; i < n; i++) {
      var bit = n >> 1;
      for (; j & bit; bit >>= 1) j ^= bit;
      j ^= bit;
      if (i < j) { var tr = re[i]; re[i] = re[j]; re[j] = tr; var ti = im[i]; im[i] = im[j]; im[j] = ti; }
    }
    for (var len = 2; len <= n; len <<= 1) {
      var step = n / len, halfL = len >> 1;
      for (var s = 0; s < n; s += len) {
        for (var k = 0; k < halfL; k++) {
          var tw = k * step;
          var wr = cosT[tw], wi = inverse ? sinT[tw] : -sinT[tw];
          var i1 = s + k, i2 = i1 + halfL;
          var ar = wr * re[i2] - wi * im[i2];
          var ai = wr * im[i2] + wi * re[i2];
          re[i2] = re[i1] - ar; im[i2] = im[i1] - ai;
          re[i1] += ar; im[i1] += ai;
        }
      }
    }
    if (inverse) { for (var x = 0; x < n; x++) { re[x] /= n; im[x] /= n; } }
  }

  // Formant preservation. A naive pitch shift moves the formants (the voice's
  // resonances) with the pitch, so a big down-shift sounds like a slowed-down
  // monster and loses presence. Fix: STFT the ORIGINAL (x) and the shifted (y),
  // estimate the original formant envelope by cepstral liftering, and multiply
  // Y by envO[k] / envO[k/r] — restoring the original envelope while keeping y's
  // pitch + timing. x and y are the same length (WSOLA preserves duration).
  function formantCorrect(x, y, fs, r) {
    var N = 1024, H = 256, L = y.length;
    if (L < N) return y; // too short to frame — leave as-is
    var half = N >> 1;
    var Q = Math.max(8, Math.round(34 * fs / 22050)); // cepstral lifter cutoff (quefrency bins)
    var GMIN = 0.1, GMAX = 10;                          // gain clamp — avoid blow-ups
    // Up-shift log-domain gain compression: at +11 the raw envelope-ratio slams
    // >half the bins into the clamp (thin highs + boomy lows = robotic/inaudible).
    // BETA<1 shrinks the correction toward unity, more the higher the shift;
    // down-shifts (the Darth-Vader case this was built for) stay full strength.
    var semisUp = r > 1 ? 12 * Math.log(r) / Math.LN2 : 0;
    var BETA = 1 / (1 + 0.10 * Math.max(0, semisUp - 3)); // 1 for |N|<=3/down, ~0.556 at +11
    var gPrev = null, GALPHA = 0.5;                      // temporal smoothing of the per-bin gain (de-jitter)
    var cosT = new Float32Array(N), sinT = new Float32Array(N);
    for (var t = 0; t < N; t++) { cosT[t] = Math.cos(2 * Math.PI * t / N); sinT[t] = Math.sin(2 * Math.PI * t / N); }
    var win = new Float32Array(N);
    for (var w = 0; w < N; w++) win[w] = 0.5 * (1 - Math.cos(2 * Math.PI * w / N)); // Hann
    var out = new Float32Array(L), norm = new Float32Array(L);
    var xr = new Float32Array(N), xi = new Float32Array(N);
    var yr = new Float32Array(N), yi = new Float32Array(N);
    var cr = new Float32Array(N), ci = new Float32Array(N);
    var env = new Float32Array(N);
    var i;
    for (var pos = 0; pos + N <= L; pos += H) {
      for (i = 0; i < N; i++) { xr[i] = x[pos + i] * win[i]; xi[i] = 0; yr[i] = y[pos + i] * win[i]; yi[i] = 0; }
      fft(xr, xi, false, cosT, sinT, N);
      fft(yr, yi, false, cosT, sinT, N);
      for (i = 0; i < N; i++) { var mag = Math.sqrt(xr[i] * xr[i] + xi[i] * xi[i]); cr[i] = Math.log(mag + 1e-8); ci[i] = 0; }
      fft(cr, ci, true, cosT, sinT, N);            // real cepstrum
      for (i = 0; i < N; i++) { if (i > Q && i < N - Q) { cr[i] = 0; ci[i] = 0; } } // lifter -> smooth envelope
      fft(cr, ci, false, cosT, sinT, N);           // smoothed log-envelope
      for (i = 0; i < N; i++) env[i] = Math.exp(cr[i]);
      var Eb = 0; for (i = 0; i < N; i++) Eb += yr[i] * yr[i] + yi[i] * yi[i]; // frame energy before gains
      var gCur = new Float32Array(half + 1);
      for (i = 0; i <= half; i++) {                // symmetric gain (real signal)
        var fp = i / r, si = Math.floor(fp), sf = fp - si; // linear-interpolated envelope lookup (no stair-step)
        if (si < 0) { si = 0; sf = 0; } else if (si >= half) { si = half; sf = 0; }
        var e0 = env[si], e1 = (si + 1 <= half) ? env[si + 1] : env[si];
        var esrc = e0 + (e1 - e0) * sf;
        var g = esrc > 1e-8 ? env[i] / esrc : 1;
        if (BETA < 1) g = Math.exp(BETA * Math.log(g)); // log-domain compression (up-shifts)
        if (g < GMIN) g = GMIN; else if (g > GMAX) g = GMAX;
        if (gPrev) g = GALPHA * gPrev[i] + (1 - GALPHA) * g; // temporal de-jitter
        gCur[i] = g;
        yr[i] *= g; yi[i] *= g;
        if (i > 0 && i < half) { yr[N - i] *= g; yi[N - i] *= g; }
      }
      gPrev = gCur;
      var Ea = 0; for (i = 0; i < N; i++) Ea += yr[i] * yr[i] + yi[i] * yi[i]; // energy after gains
      if (Ea > 1e-12) { var es = Math.sqrt(Eb / Ea); if (es > 4) es = 4; else if (es < 0.25) es = 0.25; for (i = 0; i < N; i++) { yr[i] *= es; yi[i] *= es; } } // conserve frame energy (reshape tilt only)
      fft(yr, yi, true, cosT, sinT, N);
      for (i = 0; i < N; i++) { out[pos + i] += yr[i] * win[i]; norm[pos + i] += win[i] * win[i]; }
    }
    for (i = 0; i < L; i++) out[i] = norm[i] > 1e-6 ? out[i] / norm[i] : y[i];
    return out;
  }

  // Loudness parity: scale the processed signal back to the INPUT's RMS.
  // Every stage loses a little energy nothing restores — mono mixdown, the
  // anti-alias low-pass, WSOLA's equal-gain crossfade seams — and the per-frame
  // energy conservation inside formantCorrect only runs for |n|>=3 and only
  // conserves each frame against its own pre-gain state. Net effect: tuned
  // clips played noticeably quieter than the curated originals ("the reference
  // volume is too low"). Restoring input RMS (peak-guarded so it can never
  // clip) makes a tuned clip exactly as loud as the original.
  function rmsParity(inPcm, out) {
    var i, si = 0, so = 0, peak = 0;
    for (i = 0; i < inPcm.length; i++) si += inPcm[i] * inPcm[i];
    for (i = 0; i < out.length; i++) {
      so += out[i] * out[i];
      var a = out[i] < 0 ? -out[i] : out[i];
      if (a > peak) peak = a;
    }
    var rin = Math.sqrt(si / (inPcm.length || 1));
    var rout = Math.sqrt(so / (out.length || 1));
    if (!(rin > 1e-5) || !(rout > 1e-5)) return out;
    var g = rin / rout;
    if (g > 4) g = 4; else if (g < 0.25) g = 0.25;   // sanity clamp
    if (peak * g > 0.98) g = 0.98 / peak;            // never clip
    if (Math.abs(g - 1) < 0.01) return out;          // already at parity
    for (i = 0; i < out.length; i++) out[i] *= g;
    return out;
  }

  // Full synchronous pipeline (worker entry point).
  function shiftKernel(pcm, fs, n) {
    var r = Math.pow(2, n / 12);
    var aa = r > 1 ? antiAlias(pcm, fs, r) : pcm;
    var y = resampleLinear(aa, r);
    var st = wsolaInit(y, pcm.length, fs);
    wsolaAdvance(st, 1e9);
    var out = st.out;
    // Preserve formants on larger shifts (esp. big down-shifts) so the voice
    // stays natural + present instead of "Darth Vader". Small shifts skip it —
    // the artifact is inaudible there and the naive shift is cleaner.
    if (Math.abs(n) >= 3) out = formantCorrect(pcm, out, fs, r);
    return rmsParity(pcm, out);
  }

  /* ---------------------------------------------------------------- *
   * Worker source — the kernels above, stringified, plus a message
   * handler. Built once, reused for a fresh Worker per call.
   * ---------------------------------------------------------------- */

  var WORKER_SRC = [biquadPass, antiAlias, resampleLinear, wsolaInit, wsolaAdvance, encodeWav, fft, formantCorrect, rmsParity, shiftKernel]
    .map(function (f) { return f.toString(); }).join('\n') +
    '\nself.onmessage=function(e){var d=e.data;try{var out=shiftKernel(d.pcm,d.fs,d.n);' +
    'var wav=encodeWav(out,d.fs);self.postMessage(wav,[wav]);}catch(err){self.postMessage(null);}};';

  var _workerUrl = null;
  function getWorkerUrl() {
    if (_workerUrl) return _workerUrl;
    _workerUrl = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'application/javascript' }));
    return _workerUrl;
  }

  function track(stage) {
    try { if (window.SaifTrack) window.SaifTrack('practice_transpose_failed', { stage: stage }); } catch (e) {}
  }

  /* ---------------------------------------------------------------- *
   * Decode + mixdown
   * ---------------------------------------------------------------- */

  function decode(ab) {
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return Promise.resolve(null);
    var ac;
    try { ac = new Ctx(); } catch (e) { return Promise.resolve(null); }
    return new Promise(function (resolve) {
      var settled = false;
      function done(v) { if (settled) return; settled = true; try { ac.close(); } catch (e) {} resolve(v); }
      try {
        var p = ac.decodeAudioData(ab, function (b) { done(b); }, function () { done(null); });
        if (p && p.then) p.then(function (b) { done(b); }, function () { done(null); });
      } catch (e) { done(null); }
    });
  }

  // Mix to mono at 22050 via OfflineAudioContext; fall back to a manual
  // average at the decoded rate on engines that reject 22050 offline contexts.
  function mixMono(decoded) {
    try {
      var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      if (!OAC) throw new Error('no-oac');
      var len = Math.max(1, Math.ceil(decoded.duration * TARGET_FS));
      var oac = new OAC(1, len, TARGET_FS);
      var src = oac.createBufferSource();
      src.buffer = decoded;
      src.connect(oac.destination);
      src.start(0);
      return oac.startRendering().then(function (rendered) {
        return { pcm: new Float32Array(rendered.getChannelData(0)), fs: TARGET_FS };
      }, function () { return manualMono(decoded); });
    } catch (e) {
      return Promise.resolve(manualMono(decoded));
    }
  }

  function manualMono(decoded) {
    var fs = decoded.sampleRate;
    var chs = decoded.numberOfChannels;
    var n = decoded.length;
    var out = new Float32Array(n);
    for (var c = 0; c < chs; c++) {
      var d = decoded.getChannelData(c);
      for (var i = 0; i < n; i++) out[i] += d[i];
    }
    if (chs > 1) for (var j = 0; j < n; j++) out[j] /= chs;
    return { pcm: out, fs: fs };
  }

  /* ---------------------------------------------------------------- *
   * DSP execution — worker with watchdog, chunked main-thread fallback
   * ---------------------------------------------------------------- */

  function mainThreadDsp(pcm, fs, n) {
    return new Promise(function (resolve) {
      var r, aa, y, st;
      try {
        r = Math.pow(2, n / 12);
        aa = r > 1 ? antiAlias(pcm, fs, r) : pcm;
        y = resampleLinear(aa, r);
        st = wsolaInit(y, pcm.length, fs);
      } catch (e) { resolve(null); return; }
      function step() {
        try {
          if (wsolaAdvance(st, 40)) {
            var out = st.out;
            // Match the worker path exactly — formant-correct larger shifts so
            // CSP-restricted (worker-blocked) hosts don't silently get the raw
            // "Darth Vader" down-shift. One synchronous FFT pass in the rare
            // fallback is an acceptable cost for identical output on both paths.
            if (Math.abs(n) >= 3) out = formantCorrect(pcm, out, fs, r);
            out = rmsParity(pcm, out); // keep loudness parity identical to the worker path
            resolve(encodeWav(out, fs));
            return;
          }
        } catch (e) { resolve(null); return; }
        setTimeout(step, 0);
      }
      step();
    });
  }

  function runDsp(pcm, fs, n) {
    return new Promise(function (resolve) {
      var worker = null, settled = false, watchdog = null;
      function fallback() {
        mainThreadDsp(pcm, fs, n).then(resolve, function () { resolve(null); });
      }
      try {
        worker = new Worker(getWorkerUrl());
      } catch (e) {
        // CSP blocked blob workers — run on the main thread instead.
        fallback();
        return;
      }
      watchdog = setTimeout(function () {
        if (settled) return; settled = true;
        try { worker.terminate(); } catch (e) {}
        track('timeout'); resolve(null);
      }, WATCHDOG_MS);
      worker.onmessage = function (e) {
        if (settled) return; settled = true;
        clearTimeout(watchdog);
        try { worker.terminate(); } catch (er) {}
        resolve(e.data || null);
      };
      worker.onerror = function () {
        if (settled) return; settled = true;
        clearTimeout(watchdog);
        try { worker.terminate(); } catch (er) {}
        fallback();
      };
      try {
        var copy = new Float32Array(pcm);
        worker.postMessage({ pcm: copy, fs: fs, n: n }, [copy.buffer]);
      } catch (e) {
        if (settled) return; settled = true;
        clearTimeout(watchdog);
        try { worker.terminate(); } catch (er) {}
        fallback();
      }
    });
  }

  /* ---------------------------------------------------------------- *
   * LRU cache (owns blob-URL lifecycle) + in-flight de-dup
   * ---------------------------------------------------------------- */

  var CACHE = new Map();     // key "url|N" -> blobUrl (insertion order = LRU)
  var INFLIGHT = new Map();

  function cacheGet(k) {
    if (!CACHE.has(k)) return null;
    var v = CACHE.get(k);
    CACHE.delete(k); CACHE.set(k, v); // touch -> most-recent
    return v;
  }

  function cachePut(k, v) {
    if (CACHE.has(k)) CACHE.delete(k);
    CACHE.set(k, v);
    while (CACHE.size > CACHE_CAP) {
      var oldest = CACHE.keys().next().value;
      var url = CACHE.get(oldest);
      CACHE.delete(oldest);
      try { URL.revokeObjectURL(url); } catch (e) {}
    }
  }

  function run(url, n, key) {
    return fetch(url, { mode: 'cors' }).then(function (resp) {
      if (!resp || !resp.ok) return null;
      return resp.arrayBuffer();
    }).then(function (ab) {
      if (!ab) return null;
      return decode(ab);
    }).then(function (decoded) {
      if (!decoded) return null;
      return mixMono(decoded);
    }).then(function (mixed) {
      if (!mixed || !mixed.pcm || !mixed.pcm.length) return null;
      return runDsp(mixed.pcm, mixed.fs, n);
    }).then(function (wav) {
      if (!wav) return null;
      var blobUrl = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
      cachePut(key, blobUrl);
      return blobUrl;
    }).catch(function () {
      track('exception');
      return null;
    });
  }

  function process(url, semitones) {
    var n = Math.round(Number(semitones));
    if (!url || !n || !isFinite(n) || Math.abs(n) > 12) return Promise.resolve(null);
    var key = url + '|' + n;
    var hit = cacheGet(key);
    if (hit) return Promise.resolve(hit);
    if (INFLIGHT.has(key)) return INFLIGHT.get(key);
    var p = run(url, n, key).then(function (res) {
      INFLIGHT.delete(key); return res;
    }, function () {
      INFLIGHT.delete(key); return null;
    });
    INFLIGHT.set(key, p);
    return p;
  }

  window.SaifPitchShift = { process: process };
})();
